import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  addDoc,
  collection,
  serverTimestamp,
  doc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  where,
} from "firebase/firestore";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import interactionPlugin from "@fullcalendar/interaction";

import { db } from "../firebase/firebase";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

const HOURS = ["09:00", "10:00", "11:00", "13:00", "14:00", "15:00", "16:00"];

export default function Admin() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [toast, setToast] = useState(null);
  const [activeTab, setActiveTab] = useState("dashboard");
  const [checkingRole, setCheckingRole] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);

  // BLOG
  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [imageUrl, setImageUrl] = useState("");
  const [posts, setPosts] = useState([]);
  const [showBlogModal, setShowBlogModal] = useState(false);

  // EDIT BLOG
  const [editingPost, setEditingPost] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // USERS
  const [users, setUsers] = useState([]);

  // APPOINTMENTS
  const [appointments, setAppointments] = useState([]);
  const [appointmentSearch, setAppointmentSearch] = useState("");
  const [appointmentStatusFilter, setAppointmentStatusFilter] = useState("all");
  const [showAppointmentModal, setShowAppointmentModal] = useState(false);
  const [appointmentForm, setAppointmentForm] = useState({
    userUid: "",
    fullName: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    note: "",
  });

  // USERS FILTERS
  const [userSearch, setUserSearch] = useState("");
  const [userRoleFilter, setUserRoleFilter] = useState("all");
  const [userSort, setUserSort] = useState("name_asc");

  // TRAININGS (Eğitimler)
  const [trainings, setTrainings] = useState([]);
  const [trainingTitle, setTrainingTitle] = useState("");
  const [trainingDescription, setTrainingDescription] = useState("");
  const [trainingPrice, setTrainingPrice] = useState("");
  const [trainingImageUrl, setTrainingImageUrl] = useState("");
  const [trainingLink, setTrainingLink] = useState("");
  const [showTrainingModal, setShowTrainingModal] = useState(false);
  const [editingTraining, setEditingTraining] = useState(null);
  const [showEditTrainingModal, setShowEditTrainingModal] = useState(false);
  const [trainingSearch, setTrainingSearch] = useState("");

  // BLOG FILTERS
  const [postSearch, setPostSearch] = useState("");
  const [postAuthorFilter, setPostAuthorFilter] = useState("all");
  const [postSort, setPostSort] = useState("newest");

  // CONFIRM MODAL
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "Emin misiniz?",
    message: "Bu işlem geri alınamaz.",
    confirmText: "Evet, devam et",
    cancelText: "Vazgeç",
    tone: "danger",
    onConfirm: null,
  });

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 2800);
  };

  const getStatusLabel = (status, isPast) => {
  if (status === "approved") return "Onaylandı";
  if (status === "pending") return "Bekliyor";
  if (status === "cancelled" && isPast) return "İptal Edildi (Geçmiş)";
  if (status === "cancelled") return "İptal Edildi";
  if (status === "booked") return "Rezerve";
  if (status === "expired") return "Tarihi Geçmiş";
  return "Bekliyor";
};
  const openConfirm = ({
    title,
    message,
    onConfirm,
    confirmText,
    cancelText,
    tone,
  }) => {
    setConfirmModal({
      open: true,
      title: title || "Emin misiniz?",
      message: message || "Bu işlem geri alınamaz.",
      confirmText: confirmText || "Evet, devam et",
      cancelText: cancelText || "Vazgeç",
      tone: tone || "danger",
      onConfirm,
    });
  };

  const closeConfirm = () => {
    setConfirmModal((p) => ({ ...p, open: false, onConfirm: null }));
  };

  const fetchUsers = async () => {
    const snap = await getDocs(collection(db, "users"));
    setUsers(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchPosts = async () => {
    const snap = await getDocs(collection(db, "posts"));
    setPosts(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchTrainings = async () => {
    const snap = await getDocs(collection(db, "trainings"));
    setTrainings(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
  };

  const fetchAppointments = async () => {
  const snap = await getDocs(collection(db, "appointments"));

  const isAppointmentPast = (appointment) => {
    if (!appointment?.date) return false;

    const [year, month, day] = appointment.date.split("-");
    if (!year || !month || !day) return false;

    let hours = 23;
    let minutes = 59;

    if (appointment.time) {
      const [h, m] = appointment.time.split(":");
      if (!Number.isNaN(Number(h))) hours = Number(h);
      if (!Number.isNaN(Number(m))) minutes = Number(m);
    }

    const appointmentDate = new Date(
      Number(year),
      Number(month) - 1,
      Number(day),
      hours,
      minutes,
      59
    );

    return appointmentDate.getTime() < Date.now();
  };

  const data = snap.docs.map((d) => ({
    id: d.id,
    ...d.data(),
  }));

  const updates = [];

  data.forEach((appointment) => {
    const currentStatus = appointment.status || "pending";
    const isPast = isAppointmentPast(appointment);

    const shouldExpire =
      ["pending", "approved", "booked"].includes(currentStatus) && isPast;

    if (shouldExpire) {
      updates.push(
        updateDoc(doc(db, "appointments", appointment.id), {
          status: "expired",
          updatedAt: serverTimestamp(),
        })
      );

      appointment.status = "expired";
    }

    appointment._isPast = isPast;
  });

  if (updates.length > 0) {
    await Promise.all(updates);
  }

  setAppointments(data);
};
  useEffect(() => {
    let cancelled = false;

    if (authLoading) return;

    if (!user) {
      setIsAdmin(false);
      setCheckingRole(false);
      navigate("/", { replace: true });
      return;
    }

    const checkRole = async () => {
      try {
        const userRef = doc(db, "users", user.uid);
        const snap = await getDoc(userRef);

        if (cancelled) return;

        if (snap.exists() && snap.data().role === "admin") {
          setIsAdmin(true);
          await Promise.all([fetchUsers(), fetchPosts(), fetchAppointments(), fetchTrainings()]);
        } else {
          setIsAdmin(false);
          navigate("/", { replace: true });
        }
      } catch (err) {
        if (!cancelled) {
          console.error(err);
          showToast("error", "Yetki kontrolü başarısız");
        }
      } finally {
        if (!cancelled) setCheckingRole(false);
      }
    };

    checkRole();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (title.trim().length < 5) {
      return showToast("error", "Başlık en az 5 karakter");
    }
    if (content.trim().length < 20) {
      return showToast("error", "İçerik en az 20 karakter");
    }
    if (!user) return showToast("error", "Oturum bulunamadı");

    try {
      await addDoc(collection(db, "posts"), {
        title: title.trim(),
        content: content.trim(),
        imageUrl: imageUrl.trim(),
        authorEmail: user.email,
        authorUid: user.uid,
        createdAt: serverTimestamp(),
      });

      resetBlogState();
      showToast("success", "Blog yayınlandı 🚀");
      fetchPosts();
    } catch (err) {
      console.error(err);
      showToast("error", "Blog eklenemedi (izin/rules kontrol et)");
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    if (!editingPost?.id) return;

    try {
      await updateDoc(doc(db, "posts", editingPost.id), {
        title: title.trim(),
        content: content.trim(),
        imageUrl: imageUrl.trim(),
        updatedAt: serverTimestamp(),
      });

      resetBlogState();
      showToast("success", "Blog güncellendi ✨");
      fetchPosts();
    } catch (err) {
      console.error(err);
      showToast("error", "Blog güncellenemedi (izin/rules kontrol et)");
    }
  };

  const openEditModal = (post) => {
    setEditingPost(post);
    setTitle(post.title || "");
    setContent(post.content || "");
    setImageUrl(post.imageUrl || "");
    setShowEditModal(true);
  };

  const deletePost = async (postId) => {
    try {
      await deleteDoc(doc(db, "posts", postId));
      showToast("success", "Blog silindi");
      fetchPosts();
    } catch (err) {
      console.error(err);
      showToast("error", "Blog silinemedi (izin/rules kontrol et)");
    }
  };

  const toggleRole = async (u) => {
    const nextRole = u.role === "admin" ? "user" : "admin";
    try {
      await updateDoc(doc(db, "users", u.id), {
        role: nextRole,
        updatedAt: serverTimestamp(),
      });
      showToast("success", `${u.name || u.email} → ${nextRole}`);
      fetchUsers();
    } catch (err) {
      console.error(err);
      showToast("error", "Rol değişmedi (izin/rules kontrol et)");
    }
  };

  const updateAppointmentStatus = async (appointment, nextStatus) => {
    try {
      if (
        appointment.status === "cancelled" &&
        appointment.cancelledBy === "user" &&
        nextStatus === "approved"
      ) {
        showToast(
          "error",
          "Kullanıcının iptal ettiği randevu tekrar onaylanamaz.",
        );
        return;
      }

      const payload = {
        status: nextStatus,
        updatedAt: serverTimestamp(),
      };

      if (nextStatus === "cancelled") {
        payload.cancelledBy = "admin";
      }

      if (nextStatus === "approved") {
        payload.cancelledBy = null;
      }

      await updateDoc(doc(db, "appointments", appointment.id), payload);

      showToast("success", `Randevu durumu → ${nextStatus}`);
      fetchAppointments();
    } catch (err) {
      console.error(err);
      showToast("error", "Randevu durumu güncellenemedi");
    }
  };

  const handleSelectUserForAppointment = (uid) => {
    const selectedUser = users.find((u) => (u.uid || u.id) === uid);
    if (!selectedUser) return;

    setAppointmentForm((prev) => ({
      ...prev,
      userUid: selectedUser.uid || selectedUser.id,
      fullName: selectedUser.name || "",
      email: selectedUser.email || "",
    }));
  };

  const createAppointmentForUser = async (e) => {
    e.preventDefault();

    try {
      if (!appointmentForm.userUid) {
        return showToast("error", "Lütfen kayıtlı bir kullanıcı seçin.");
      }

      if (!appointmentForm.date || !appointmentForm.time) {
        return showToast("error", "Tarih ve saat seçin.");
      }

      const selectedUserAppointmentsQuery = query(
        collection(db, "appointments"),
        where("userUid", "==", appointmentForm.userUid),
      );

      const selectedUserAppointmentsSnap = await getDocs(
        selectedUserAppointmentsQuery,
      );

      const hasActiveAppointment = selectedUserAppointmentsSnap.docs
        .map((d) => d.data())
        .some(
          (item) =>
            item.status === "pending" ||
            item.status === "approved" ||
            item.status === "booked",
        );

      if (hasActiveAppointment) {
        return showToast(
          "error",
          "Bu kullanıcının zaten aktif bir randevusu var.",
        );
      }

      const sameSlotQuery = query(
        collection(db, "appointments"),
        where("date", "==", appointmentForm.date),
      );

      const sameSlotSnap = await getDocs(sameSlotQuery);

      const slotTaken = sameSlotSnap.docs
        .map((d) => d.data())
        .some(
          (item) =>
            item.time === appointmentForm.time &&
            (item.status === "pending" ||
              item.status === "approved" ||
              item.status === "booked"),
        );

      if (slotTaken) {
        return showToast("error", "Bu saat başka bir randevu tarafından dolu.");
      }

      await addDoc(collection(db, "appointments"), {
        userUid: appointmentForm.userUid,
        userEmail: appointmentForm.email,
        fullName: appointmentForm.fullName,
        email: appointmentForm.email,
        phone: appointmentForm.phone,
        date: appointmentForm.date,
        time: appointmentForm.time,
        note: appointmentForm.note,
        status: "approved",
        createdBy: "admin",
        createdAt: serverTimestamp(),
      });

      showToast("success", "Kullanıcı için randevu oluşturuldu.");
      resetAppointmentForm();
      fetchAppointments();
    } catch (err) {
      console.error(err);
      showToast("error", "Randevu oluşturulamadı.");
    }
  };

  // TRAINING CRUD
  const handleTrainingSubmit = async (e) => {
    e.preventDefault();
    if (trainingTitle.trim().length < 3) return showToast("error", "Başlık en az 3 karakter");
    if (trainingDescription.trim().length < 10) return showToast("error", "Açıklama en az 10 karakter");

    try {
      await addDoc(collection(db, "trainings"), {
        title: trainingTitle.trim(),
        description: trainingDescription.trim(),
        price: trainingPrice.trim(),
        imageUrl: trainingImageUrl.trim(),
        link: trainingLink.trim(),
        authorEmail: user.email,
        createdAt: serverTimestamp(),
      });
      resetTrainingState();
      showToast("success", "Eğitim eklendi");
      fetchTrainings();
    } catch (err) {
      console.error(err);
      showToast("error", "Eğitim eklenemedi");
    }
  };

  const handleTrainingUpdate = async (e) => {
    e.preventDefault();
    if (!editingTraining?.id) return;
    try {
      await updateDoc(doc(db, "trainings", editingTraining.id), {
        title: trainingTitle.trim(),
        description: trainingDescription.trim(),
        price: trainingPrice.trim(),
        imageUrl: trainingImageUrl.trim(),
        link: trainingLink.trim(),
        updatedAt: serverTimestamp(),
      });
      resetTrainingState();
      showToast("success", "Eğitim güncellendi");
      fetchTrainings();
    } catch (err) {
      console.error(err);
      showToast("error", "Eğitim güncellenemedi");
    }
  };

  const openEditTrainingModal = (t) => {
    setEditingTraining(t);
    setTrainingTitle(t.title || "");
    setTrainingDescription(t.description || "");
    setTrainingPrice(t.price || "");
    setTrainingImageUrl(t.imageUrl || "");
    setTrainingLink(t.link || "");
    setShowEditTrainingModal(true);
  };

  const deleteTraining = async (id) => {
    try {
      await deleteDoc(doc(db, "trainings", id));
      showToast("success", "Eğitim silindi");
      fetchTrainings();
    } catch (err) {
      console.error(err);
      showToast("error", "Eğitim silinemedi");
    }
  };

  const resetTrainingState = () => {
    setTrainingTitle("");
    setTrainingDescription("");
    setTrainingPrice("");
    setTrainingImageUrl("");
    setTrainingLink("");
    setShowTrainingModal(false);
    setShowEditTrainingModal(false);
    setEditingTraining(null);
  };

  const filteredTrainings = trainings.filter((t) => {
    const s = trainingSearch.toLowerCase();
    return !s || (t.title || "").toLowerCase().includes(s) || (t.description || "").toLowerCase().includes(s);
  });

  const resetBlogState = () => {
    setTitle("");
    setContent("");
    setImageUrl("");
    setShowBlogModal(false);
    setShowEditModal(false);
    setEditingPost(null);
  };

  const resetAppointmentForm = () => {
    setAppointmentForm({
      userUid: "",
      fullName: "",
      email: "",
      phone: "",
      date: "",
      time: "",
      note: "",
    });
    setShowAppointmentModal(false);
  };

  const filteredUsers = useMemo(() => {
    return users
      .filter((u) => {
        const q = userSearch.trim().toLowerCase();
        const matchesSearch =
          !q ||
          (u.name || "").toLowerCase().includes(q) ||
          (u.email || "").toLowerCase().includes(q) ||
          (u.uid || "").toLowerCase().includes(q) ||
          (u.id || "").toLowerCase().includes(q);

        const matchesRole =
          userRoleFilter === "all" ? true : u.role === userRoleFilter;
        return matchesSearch && matchesRole;
      })
      .sort((a, b) => {
        if (userSort === "role")
          return (a.role || "").localeCompare(b.role || "");
        if (userSort === "name_desc") {
          return (b.name || "").localeCompare(a.name || "");
        }
        return (a.name || "").localeCompare(b.name || "");
      });
  }, [users, userSearch, userRoleFilter, userSort]);

  const postAuthors = useMemo(() => {
    const authors = posts.map((p) => p.authorEmail || p.author).filter(Boolean);
    return Array.from(new Set(authors));
  }, [posts]);

  const filteredPosts = useMemo(() => {
    return posts
      .filter((p) => {
        const q = postSearch.trim().toLowerCase();
        const author = (p.authorEmail || p.author || "").toLowerCase();

        const matchesSearch =
          !q ||
          (p.title || "").toLowerCase().includes(q) ||
          (p.content || "").toLowerCase().includes(q) ||
          author.includes(q);

        const matchesAuthor =
          postAuthorFilter === "all"
            ? true
            : (p.authorEmail || p.author) === postAuthorFilter;

        return matchesSearch && matchesAuthor;
      })
      .sort((a, b) => {
        const aTime = a?.createdAt?.seconds ?? 0;
        const bTime = b?.createdAt?.seconds ?? 0;

        if (postSort === "oldest") return aTime - bTime;
        if (postSort === "title_asc") {
          return (a.title || "").localeCompare(b.title || "");
        }
        return bTime - aTime;
      });
  }, [posts, postSearch, postAuthorFilter, postSort]);

  const filteredAppointments = useMemo(() => {
    return appointments
      .filter((a) => {
        const q = appointmentSearch.trim().toLowerCase();

        const matchesSearch =
          !q ||
          (a.fullName || "").toLowerCase().includes(q) ||
          (a.phone || "").toLowerCase().includes(q) ||
          (a.email || "").toLowerCase().includes(q) ||
          (a.date || "").toLowerCase().includes(q) ||
          (a.time || "").toLowerCase().includes(q);

        const matchesStatus =
          appointmentStatusFilter === "all"
            ? true
            : (a.status || "pending") === appointmentStatusFilter;

        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        const aTime = a?.createdAt?.seconds ?? 0;
        const bTime = b?.createdAt?.seconds ?? 0;
        return bTime - aTime;
      });
  }, [appointments, appointmentSearch, appointmentStatusFilter]);

  const calendarEvents = useMemo(() => {
    return appointments
      .filter((item) => item.date && item.time)
      .map((item) => {
        const start = `${item.date}T${item.time}:00`;

        let backgroundColor = "#f59e0b";
        let borderColor = "#f59e0b";
        let textColor = "#ffffff";

        if (item.status === "approved") {
          backgroundColor = "#10b981";
          borderColor = "#10b981";
        }

        if (item.status === "cancelled") {
          backgroundColor = "#ef4444";
          borderColor = "#ef4444";
        }

        if (item.status === "booked") {
          backgroundColor = "#3b82f6";
          borderColor = "#3b82f6";
        }

        return {
          id: item.id,
          title: `${item.time} • ${item.fullName || "Randevu"}`,
          start,
          allDay: false,
          backgroundColor,
          borderColor,
          textColor,
          extendedProps: {
            phone: item.phone || "",
            email: item.email || item.userEmail || "",
            status: item.status || "pending",
            note: item.note || "",
            fullName: item.fullName || "",
            date: item.date || "",
            time: item.time || "",
          },
        };
      });
  }, [appointments]);

  const adminsCount = useMemo(
    () => users.filter((u) => u.role === "admin").length,
    [users],
  );

  const latestUsers = useMemo(() => {
    return [...users]
      .sort(
        (a, b) => (b?.createdAt?.seconds ?? 0) - (a?.createdAt?.seconds ?? 0),
      )
      .slice(0, 5);
  }, [users]);

  const latestPosts = useMemo(() => {
    return [...posts]
      .sort(
        (a, b) => (b?.createdAt?.seconds ?? 0) - (a?.createdAt?.seconds ?? 0),
      )
      .slice(0, 5);
  }, [posts]);

  if (authLoading || checkingRole) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-black/5 px-6 py-4">
          Yükleniyor...
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-black/5 px-6 py-4">
          Yönlendiriliyorsun...
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="bg-white rounded-2xl shadow-sm ring-1 ring-black/5 px-6 py-4 text-red-600 font-semibold">
          Erişim yok
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex">
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -18 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className={`fixed top-5 right-5 px-5 py-3 rounded-2xl text-white shadow-lg ${
              toast.type === "success" ? "bg-emerald-600" : "bg-rose-600"
            }`}
          >
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <aside className="w-72 bg-white border-r border-gray-200 p-6">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-900">
            Admin <span className="text-pink-600">Panel</span>
          </h1>
          <span className="text-[11px] px-2 py-1 rounded-full bg-pink-50 text-pink-700 ring-1 ring-pink-200">
            Admin
          </span>
        </div>

        <div className="mt-6 space-y-2">
          <NavBtn
            active={activeTab === "dashboard"}
            onClick={() => setActiveTab("dashboard")}
          >
            Dashboard
          </NavBtn>
          <NavBtn
            active={activeTab === "blog"}
            onClick={() => setActiveTab("blog")}
          >
            Blog
          </NavBtn>
          <NavBtn
            active={activeTab === "trainings"}
            onClick={() => setActiveTab("trainings")}
          >
            Eğitimler
          </NavBtn>
          <NavBtn
            active={activeTab === "users"}
            onClick={() => setActiveTab("users")}
          >
            Kullanıcılar
          </NavBtn>
          <NavBtn
            active={activeTab === "appointments"}
            onClick={() => setActiveTab("appointments")}
          >
            Randevular
          </NavBtn>
          <NavBtn
            active={activeTab === "calendar"}
            onClick={() => setActiveTab("calendar")}
          >
            Takvim
          </NavBtn>
        </div>

        <div className="mt-6 rounded-2xl bg-gray-50 ring-1 ring-black/5 p-4">
          <p className="text-xs text-gray-500">Giriş yapan</p>
          <p className="text-sm font-semibold text-gray-900 truncate">
            {user?.email || "—"}
          </p>
        </div>
      </aside>

      <main className="flex-1 p-6 md:p-10">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-7">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900">
              {activeTab === "dashboard"
                ? "Dashboard"
                : activeTab === "blog"
                  ? "Blog Yönetimi"
                  : activeTab === "trainings"
                    ? "Eğitim Yönetimi"
                    : activeTab === "users"
                      ? "Kullanıcı Yönetimi"
                      : activeTab === "appointments"
                        ? "Randevu Yönetimi"
                        : "Randevu Takvimi"}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Modern panel görünümü + arama/filtreleme + güvenli silme onayı
            </p>
          </div>

          <div className="flex gap-2">
            {activeTab === "blog" && (
              <PrimaryBtn onClick={() => setShowBlogModal(true)}>
                + Yeni Blog
              </PrimaryBtn>
            )}

            {activeTab === "trainings" && (
              <PrimaryBtn onClick={() => setShowTrainingModal(true)}>
                + Yeni Eğitim
              </PrimaryBtn>
            )}

            {activeTab === "appointments" && (
              <PrimaryBtn onClick={() => setShowAppointmentModal(true)}>
                + Randevu Oluştur
              </PrimaryBtn>
            )}

            {activeTab === "dashboard" && (
              <>
                <PrimaryBtn
                  onClick={() => {
                    setActiveTab("blog");
                    setShowBlogModal(true);
                  }}
                >
                  + Yeni Blog
                </PrimaryBtn>
                <NeutralBtn onClick={() => setActiveTab("users")}>
                  Kullanıcılar
                </NeutralBtn>
              </>
            )}
          </div>
        </div>

        {activeTab === "dashboard" && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <StatCard title="Toplam Kullanıcı" value={users.length} />
              <StatCard title="Toplam Blog" value={posts.length} />
              <StatCard title="Toplam Eğitim" value={trainings.length} />
              <StatCard title="Toplam Randevu" value={appointments.length} />
              <StatCard title="Admin Sayısı" value={adminsCount} />
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <PanelCard title="Son Eklenen Kullanıcılar">
                <div className="space-y-3">
                  {latestUsers.map((u) => (
                    <RowCard
                      key={u.id}
                      left={
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {u.name || "—"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {u.email}
                          </p>
                        </div>
                      }
                      right={<Pill>{u.role || "user"}</Pill>}
                    />
                  ))}
                  {latestUsers.length === 0 && (
                    <EmptyState text="Henüz kullanıcı yok." />
                  )}
                </div>
              </PanelCard>

              <PanelCard title="Son Eklenen Bloglar">
                <div className="space-y-3">
                  {latestPosts.map((p) => (
                    <RowCard
                      key={p.id}
                      left={
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 truncate">
                            {p.title || "—"}
                          </p>
                          <p className="text-xs text-gray-500 truncate">
                            {p.authorEmail || p.author || "—"}
                          </p>
                        </div>
                      }
                      right={
                        <NeutralBtn
                          onClick={() => {
                            setActiveTab("blog");
                            openEditModal(p);
                          }}
                        >
                          Düzenle
                        </NeutralBtn>
                      }
                    />
                  ))}
                  {latestPosts.length === 0 && (
                    <EmptyState text="Henüz blog yok." />
                  )}
                </div>
              </PanelCard>
            </div>
          </>
        )}

        {activeTab === "blog" && (
          <>
            <PanelCard title="Filtreler">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  value={postSearch}
                  onChange={(e) => setPostSearch(e.target.value)}
                  placeholder="Blog ara (başlık / içerik / yazar)"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200"
                />

                <select
                  value={postAuthorFilter}
                  onChange={(e) => setPostAuthorFilter(e.target.value)}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200 bg-white"
                >
                  <option value="all">Tüm Yazarlar</option>
                  {postAuthors.map((a) => (
                    <option key={a} value={a}>
                      {a}
                    </option>
                  ))}
                </select>

                <select
                  value={postSort}
                  onChange={(e) => setPostSort(e.target.value)}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200 bg-white"
                >
                  <option value="newest">En yeni</option>
                  <option value="oldest">En eski</option>
                  <option value="title_asc">Başlık (A-Z)</option>
                </select>
              </div>
            </PanelCard>

            <div className="mt-4 space-y-3">
              {filteredPosts.map((post) => (
                <RowCard
                  key={post.id}
                  left={
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {post.title}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {post.authorEmail || post.author || "—"}
                      </p>
                    </div>
                  }
                  right={
                    <>
                      <NeutralBtn onClick={() => openEditModal(post)}>
                        Düzenle
                      </NeutralBtn>
                      <DangerBtn
                        onClick={() =>
                          openConfirm({
                            title: "Blog silinsin mi?",
                            message: `"${post.title}" silinecek. Bu işlem geri alınamaz.`,
                            confirmText: "Evet, sil",
                            cancelText: "Vazgeç",
                            tone: "danger",
                            onConfirm: () => deletePost(post.id),
                          })
                        }
                      >
                        Sil
                      </DangerBtn>
                    </>
                  }
                />
              ))}
              {filteredPosts.length === 0 && (
                <EmptyState text="Sonuç bulunamadı." />
              )}
            </div>
          </>
        )}

        {activeTab === "trainings" && (
          <>
            <PanelCard title="Filtreler">
              <input
                value={trainingSearch}
                onChange={(e) => setTrainingSearch(e.target.value)}
                placeholder="Eğitim ara (başlık / açıklama)"
                className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200"
              />
            </PanelCard>

            <div className="mt-4 space-y-3">
              {filteredTrainings.map((t) => (
                <RowCard
                  key={t.id}
                  left={
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">{t.title}</p>
                      <p className="text-xs text-gray-500 truncate">
                        {t.price ? `${t.price}` : "Ücretsiz"} — {t.description?.slice(0, 60)}...
                      </p>
                    </div>
                  }
                  right={
                    <>
                      <NeutralBtn onClick={() => openEditTrainingModal(t)}>
                        Düzenle
                      </NeutralBtn>
                      <DangerBtn
                        onClick={() =>
                          openConfirm({
                            title: "Eğitim silinsin mi?",
                            message: `"${t.title}" silinecek. Bu işlem geri alınamaz.`,
                            confirmText: "Evet, sil",
                            cancelText: "Vazgeç",
                            tone: "danger",
                            onConfirm: () => deleteTraining(t.id),
                          })
                        }
                      >
                        Sil
                      </DangerBtn>
                    </>
                  }
                />
              ))}
              {filteredTrainings.length === 0 && (
                <EmptyState text="Henüz eğitim eklenmemiş." />
              )}
            </div>
          </>
        )}

        {activeTab === "users" && (
          <>
            <PanelCard title="Filtreler">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  placeholder="Kullanıcı ara (isim / email / uid)"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-200"
                />

                <select
                  value={userRoleFilter}
                  onChange={(e) => setUserRoleFilter(e.target.value)}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-200 bg-white"
                >
                  <option value="all">Tüm Roller</option>
                  <option value="admin">Admin</option>
                  <option value="user">User</option>
                </select>

                <select
                  value={userSort}
                  onChange={(e) => setUserSort(e.target.value)}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-indigo-200 bg-white"
                >
                  <option value="name_asc">İsim (A-Z)</option>
                  <option value="name_desc">İsim (Z-A)</option>
                  <option value="role">Role göre</option>
                </select>
              </div>
            </PanelCard>

            <div className="mt-4 space-y-3">
              {filteredUsers.map((u) => (
                <RowCard
                  key={u.id}
                  left={
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {u.name || "—"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {u.email || "—"}
                      </p>
                      <p className="text-[11px] text-gray-400 truncate">
                        UID: {u.uid || u.id}
                      </p>
                    </div>
                  }
                  right={
                    <NeutralBtn onClick={() => toggleRole(u)}>
                      Rol:{" "}
                      <span className="ml-1 font-semibold">
                        {u.role || "user"}
                      </span>
                    </NeutralBtn>
                  }
                />
              ))}
              {filteredUsers.length === 0 && (
                <EmptyState text="Sonuç bulunamadı." />
              )}
            </div>
          </>
        )}

        {activeTab === "appointments" && (
          <>
            <PanelCard title="Filtreler">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input
                  value={appointmentSearch}
                  onChange={(e) => setAppointmentSearch(e.target.value)}
                  placeholder="Randevu ara (isim / telefon / e-posta / tarih / saat)"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-200"
                />

                <select
  value={appointmentStatusFilter}
  onChange={(e) => setAppointmentStatusFilter(e.target.value)}
  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-200 bg-white"
>
  <option value="all">Tüm Durumlar</option>
  <option value="pending">Bekleniyor</option>
  <option value="approved">Onaylı</option>
  <option value="cancelled">İptal Edildi</option>
  <option value="expired">Tarihi Geçmiş</option>
</select>
              </div>
            </PanelCard>

            <div className="mt-4 space-y-3">
              {filteredAppointments.map((a) => (
                <RowCard
                  key={a.id}
                  left={
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 truncate">
                        {a.fullName || "—"}
                      </p>
                      <p className="text-xs text-gray-500 truncate">
                        {a.phone || "—"} {a.email ? `• ${a.email}` : ""}
                      </p>
                      <p className="text-[11px] text-gray-400 truncate">
                        {a.date || "—"} • {a.time || "—"}
                      </p>
                      {a.note && (
                        <p className="text-xs text-gray-500 mt-1 truncate">
                          Not: {a.note}
                        </p>
                      )}
                      {a.status === "cancelled" && a.cancelledBy && (
                        <p className="text-xs text-rose-500 mt-1">
                          İptal eden:{" "}
                          {a.cancelledBy === "user" ? "Kullanıcı" : "Admin"}
                        </p>
                      )}
                    </div>
                  }
                  right={
                    <>
                      <Pill>{getStatusLabel(a.status, a._isPast)}</Pill>

                      {!a._isPast &&
  (a.status === "pending" ||
    !a.status ||
    (a.status === "cancelled" &&
      a.cancelledBy !== "user")) && (
    <NeutralBtn
      onClick={() => updateAppointmentStatus(a, "approved")}
    >
      Onayla
    </NeutralBtn>
  )}

                      {(a.status === "pending" ||
                        !a.status ||
                        a.status === "approved") && (
                        <DangerBtn
                          onClick={() =>
                            updateAppointmentStatus(a, "cancelled")
                          }
                        >
                          İptal
                        </DangerBtn>
                      )}
                    </>
                  }
                />
              ))}

              {filteredAppointments.length === 0 && (
                <EmptyState text="Henüz randevu bulunamadı." />
              )}
            </div>
          </>
        )}

        {activeTab === "calendar" && (
          <div className="space-y-4">
            <PanelCard title="Randevu Takvimi">
              <div className="mb-5 flex flex-wrap gap-3">
                <div className="flex items-center gap-2 rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-700">
                  <span className="h-3 w-3 rounded-full bg-amber-500"></span>
                  Bekliyor
                </div>

                <div className="flex items-center gap-2 rounded-xl bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  <span className="h-3 w-3 rounded-full bg-emerald-500"></span>
                  Onaylandı
                </div>

                <div className="flex items-center gap-2 rounded-xl bg-rose-50 px-3 py-2 text-sm text-rose-700">
                  <span className="h-3 w-3 rounded-full bg-rose-500"></span>
                  İptal Edildi
                </div>

                
              </div>

              <div className="rounded-3xl border border-gray-200 bg-white p-4">
                <FullCalendar
                  plugins={[dayGridPlugin, interactionPlugin]}
                  initialView="dayGridMonth"
                  moreLinkContent={(args) => `+${args.num} daha fazla`}
                  displayEventTime={false}
                  locale="tr"
                  firstDay={1}
                  height="auto"
                  dayMaxEvents={3}
                  headerToolbar={{
                    left: "prev,next",
                    center: "title",
                    right: "",
                  }}
                  buttonText={{
                    today: "Bugün",
                    month: "Ay",
                  }}
                  events={calendarEvents}
                  eventClick={(info) => {
                    const { phone, email, status, note, fullName, date, time } =
                      info.event.extendedProps;

                    showToast(
                      "success",
                      `${fullName || "Randevu"} • ${date} • ${time} • ${status}${
                        phone ? ` • ${phone}` : ""
                      }${email ? ` • ${email}` : ""}${note ? ` • ${note}` : ""}`,
                    );
                  }}
                />
              </div>
            </PanelCard>
          </div>
        )}
      </main>

      <AnimatePresence>
        {(showBlogModal || showEditModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex justify-center items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              className="bg-white w-full max-w-xl rounded-3xl shadow-xl ring-1 ring-black/10 p-6 md:p-8"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {showEditModal ? "Blog Düzenle" : "Yeni Blog"}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Başlık, görsel ve içerik bilgilerini gir.
                  </p>
                </div>
                <button
                  onClick={resetBlogState}
                  className="text-gray-500 hover:text-gray-900 px-3 py-2 rounded-xl hover:bg-gray-50"
                >
                  Kapat
                </button>
              </div>

              <form
                onSubmit={showEditModal ? handleUpdate : handleSubmit}
                className="mt-6 space-y-3"
              >
                <input
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Başlık"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200"
                />
                <input
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="Görsel URL (opsiyonel)"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200"
                />
                <textarea
                  value={content}
                  onChange={(e) => setContent(e.target.value)}
                  placeholder="İçerik"
                  rows={6}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200 resize-none"
                />

                <div className="flex justify-end gap-2 pt-2">
                  <NeutralBtn type="button" onClick={resetBlogState}>
                    Vazgeç
                  </NeutralBtn>
                  <PrimaryBtn type="submit">
                    {showEditModal ? "Güncelle" : "Yayınla"}
                  </PrimaryBtn>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}

        {/* EĞİTİM MODAL */}
        {(showTrainingModal || showEditTrainingModal) && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex justify-center items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              className="bg-white w-full max-w-xl rounded-3xl shadow-xl ring-1 ring-black/10 p-6 md:p-8"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    {showEditTrainingModal ? "Eğitim Düzenle" : "Yeni Eğitim"}
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Eğitim bilgilerini girin.
                  </p>
                </div>
                <button
                  onClick={resetTrainingState}
                  className="text-gray-500 hover:text-gray-900 px-3 py-2 rounded-xl hover:bg-gray-50"
                >
                  Kapat
                </button>
              </div>

              <form
                onSubmit={showEditTrainingModal ? handleTrainingUpdate : handleTrainingSubmit}
                className="mt-6 space-y-3"
              >
                <input
                  value={trainingTitle}
                  onChange={(e) => setTrainingTitle(e.target.value)}
                  placeholder="Eğitim Başlığı"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200"
                />
                <input
                  value={trainingPrice}
                  onChange={(e) => setTrainingPrice(e.target.value)}
                  placeholder="Fiyat (örn: 1.500 TL veya Ücretsiz)"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200"
                />
                <input
                  value={trainingImageUrl}
                  onChange={(e) => setTrainingImageUrl(e.target.value)}
                  placeholder="Görsel URL (opsiyonel)"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200"
                />
                <input
                  value={trainingLink}
                  onChange={(e) => setTrainingLink(e.target.value)}
                  placeholder="Satış / Kayıt Linki (opsiyonel)"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200"
                />
                <textarea
                  value={trainingDescription}
                  onChange={(e) => setTrainingDescription(e.target.value)}
                  placeholder="Eğitim Açıklaması"
                  rows={5}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-pink-200 resize-none"
                />

                <div className="flex justify-end gap-2 pt-2">
                  <NeutralBtn type="button" onClick={resetTrainingState}>
                    Vazgeç
                  </NeutralBtn>
                  <PrimaryBtn type="submit">
                    {showEditTrainingModal ? "Güncelle" : "Ekle"}
                  </PrimaryBtn>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showAppointmentModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex justify-center items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              className="bg-white w-full max-w-xl rounded-3xl shadow-xl ring-1 ring-black/10 p-6 md:p-8"
            >
              <div className="flex items-start justify-between gap-4">
                <div>
                  <h3 className="text-xl font-bold text-gray-900">
                    Kullanıcı İçin Randevu Oluştur
                  </h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Yalnızca kayıtlı kullanıcılar için randevu ekleyebilirsiniz.
                  </p>
                </div>
                <button
                  onClick={resetAppointmentForm}
                  className="text-gray-500 hover:text-gray-900 px-3 py-2 rounded-xl hover:bg-gray-50"
                >
                  Kapat
                </button>
              </div>

              <form
                onSubmit={createAppointmentForUser}
                className="mt-6 space-y-3"
              >
                <select
                  value={appointmentForm.userUid}
                  onChange={(e) =>
                    handleSelectUserForAppointment(e.target.value)
                  }
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-200 bg-white"
                >
                  <option value="">Kullanıcı seçin</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.uid || u.id}>
                      {u.name || "İsimsiz"} - {u.email || "E-posta yok"}
                    </option>
                  ))}
                </select>

                <input
                  value={appointmentForm.fullName}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...appointmentForm,
                      fullName: e.target.value,
                    })
                  }
                  placeholder="Ad Soyad"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-200"
                />

                <input
                  value={appointmentForm.email}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...appointmentForm,
                      email: e.target.value,
                    })
                  }
                  placeholder="E-posta"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-200"
                />

                <input
                  value={appointmentForm.phone}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...appointmentForm,
                      phone: e.target.value,
                    })
                  }
                  placeholder="Telefon"
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-200"
                />

                <input
                  type="date"
                  value={appointmentForm.date}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...appointmentForm,
                      date: e.target.value,
                    })
                  }
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-200"
                />

                <select
                  value={appointmentForm.time}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...appointmentForm,
                      time: e.target.value,
                    })
                  }
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-200 bg-white"
                >
                  <option value="">Saat seçin</option>
                  {HOURS.map((hour) => (
                    <option key={hour} value={hour}>
                      {hour}
                    </option>
                  ))}
                </select>

                <textarea
                  value={appointmentForm.note}
                  onChange={(e) =>
                    setAppointmentForm({
                      ...appointmentForm,
                      note: e.target.value,
                    })
                  }
                  placeholder="Not"
                  rows={4}
                  className="w-full border border-gray-200 rounded-2xl px-4 py-3 outline-none focus:ring-2 focus:ring-emerald-200 resize-none"
                />

                <div className="flex justify-end gap-2 pt-2">
                  <NeutralBtn type="button" onClick={resetAppointmentForm}>
                    Vazgeç
                  </NeutralBtn>
                  <PrimaryBtn type="submit">Randevu Oluştur</PrimaryBtn>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {confirmModal.open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/40 flex justify-center items-center p-4"
          >
            <motion.div
              initial={{ scale: 0.96, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.98, opacity: 0 }}
              className="bg-white w-full max-w-md rounded-3xl shadow-xl ring-1 ring-black/10 p-6"
            >
              <p className="text-lg font-bold text-gray-900">
                {confirmModal.title}
              </p>
              <p className="text-sm text-gray-600 mt-2">
                {confirmModal.message}
              </p>

              <div className="flex justify-end gap-2 mt-6">
                <NeutralBtn onClick={closeConfirm}>
                  {confirmModal.cancelText}
                </NeutralBtn>
                <button
                  onClick={async () => {
                    try {
                      await confirmModal.onConfirm?.();
                    } finally {
                      closeConfirm();
                    }
                  }}
                  className={`px-4 py-2 rounded-2xl font-semibold text-white transition ${
                    confirmModal.tone === "danger"
                      ? "bg-rose-600 hover:bg-rose-500"
                      : "bg-gray-900 hover:bg-gray-800"
                  }`}
                >
                  {confirmModal.confirmText}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function NavBtn({ active, onClick, children }) {
  return (
    <button
      onClick={onClick}
      className={`w-full text-left px-4 py-3 rounded-2xl transition font-semibold ${
        active
          ? "bg-pink-50 text-pink-700 ring-1 ring-pink-200"
          : "text-gray-700 hover:bg-gray-50 ring-1 ring-transparent"
      }`}
    >
      {children}
    </button>
  );
}

function StatCard({ title, value }) {
  return (
    <div className="bg-white rounded-3xl shadow-sm ring-1 ring-black/5 p-6">
      <p className="text-sm text-gray-500">{title}</p>
      <p className="text-3xl font-extrabold text-gray-900 mt-2">{value}</p>
    </div>
  );
}

function PanelCard({ title, children }) {
  return (
    <section className="bg-white rounded-3xl shadow-sm ring-1 ring-black/5 p-5 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm font-bold text-gray-900">{title}</p>
      </div>
      {children}
    </section>
  );
}

function RowCard({ left, right }) {
  return (
    <div className="bg-white rounded-3xl shadow-sm ring-1 ring-black/5 p-4 flex items-center justify-between gap-4">
      <div className="min-w-0">{left}</div>
      <div className="flex items-center gap-2 shrink-0">{right}</div>
    </div>
  );
}

function Pill({ children }) {
  return (
    <span className="text-xs font-semibold px-3 py-1 rounded-full bg-gray-50 text-gray-700 ring-1 ring-black/5">
      {children}
    </span>
  );
}

function EmptyState({ text }) {
  return (
    <div className="bg-white rounded-3xl shadow-sm ring-1 ring-black/5 p-6 text-sm text-gray-500">
      {text}
    </div>
  );
}

function PrimaryBtn({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`px-4 py-2 rounded-2xl font-semibold text-white bg-pink-600 hover:bg-pink-500 transition ${className}`}
    >
      {children}
    </button>
  );
}

function NeutralBtn({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`px-4 py-2 rounded-2xl font-semibold text-gray-900 bg-gray-100 hover:bg-gray-200 transition ${className}`}
    >
      {children}
    </button>
  );
}

function DangerBtn({ children, className = "", ...props }) {
  return (
    <button
      {...props}
      className={`px-4 py-2 rounded-2xl font-semibold text-white bg-rose-600 hover:bg-rose-500 transition ${className}`}
    >
      {children}
    </button>
  );
}
